import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { format } from 'date-fns'

import useTableSort from '@/hooks/table/use-table-sort.hook'
import { transactionsSelector } from '@/state/transactions/slices/index.slices'
import { uiGlobalSelector } from '@/state/ui/global/slices/index.slices'

import { formatRowDate, getDateTimeFromIsoUTC } from '@/utils/date'
import { TimeZoneUTCType } from '@/enums'
import { TableHeadCellPropsType } from '@/state/ui/global/models'

import {
  ContributionsLibraryColumnProps,
  ContributionsFreePlays,
} from '@/state/transactions/models'
import { dateTimeTableFormat } from '@/constants/date'

export const useContributions = () => {
  const { t } = useTranslation()

  const { contributions, isLoading, contributionsFilters, pagination } =
    useSelector(transactionsSelector)

  const { isLoadingFull } = useSelector(uiGlobalSelector)

  let headCells: TableHeadCellPropsType<ContributionsLibraryColumnProps>[] = [
    {
      id: 'dateAtText',
      label: t('transaction.placedDate'),
      width: 120,
      sort: true,
    },
    { id: 'betId', label: t('transaction.betId'), sort: true },
    { id: 'status', label: t('transaction.sportWagerStatus'), sort: true },
    { id: 'betType', label: t('transaction.sportWagerType'), sort: true },
    { id: 'riskAmount', label: t('transaction.riskAmount'), sort: true },
    { id: 'winAmount', label: t('transaction.winAmount'), sort: true },
    {
      id: 'wasRolloverAffected',
      label: t('transaction.wasRolloverAffected'),
      sort: true,
    },
    { id: 'rollOverAmount', label: t('labels.rolloverAmount'), sort: true },
  ]

  const formattedContributions =
    contributions?.map((contribution) => {
      return {
        ...contribution,
        placedDate: contribution.placedDate
          ? formatRowDate(contribution.placedDate)
          : '-',
        dateAtText: contribution.placedDate
          ? new Date(contribution.placedDate).getTime()
          : '-',
      }
    }) || []

  const sortedInitialState = 'id'

  const { sortedRows, createSortHandler, order, orderBy } = useTableSort<
    ContributionsLibraryColumnProps,
    keyof ContributionsLibraryColumnProps
  >(formattedContributions, sortedInitialState, 'desc')

  const getXlsFormattedRow = (
    contribution: ContributionsFreePlays,
  ): ContributionsLibraryColumnProps => {
    return {
      ...contribution,
      placedDate: contribution.placedDate
        ? formatRowDate(contribution.placedDate)
        : '-',
      dateAtText: contribution.placedDate
        ? `${format(
            getDateTimeFromIsoUTC(
              contribution.placedDate,
              TimeZoneUTCType['GMT-07 Pacific Time'],
            ),
            dateTimeTableFormat,
          )} PT`
        : '-',
      status: t(`transaction.sportWagerStatusOptions.${contribution.status}`),
      betType: t(`transaction.sportWagerTypeOptions.${contribution.betType}`),
      wasRolloverAffected: contribution.wasRolloverAffected
        ? t('labels.true')
        : t('labels.false'),
    }
  }

  return {
    sortedRows,
    createSortHandler,
    order,
    orderBy,
    isLoading,
    isLoadingFull,
    headCells,
    contributionsFilters,
    pagination,
    getXlsFormattedRow,
  }
}
